<template>
  <a-modal
    :visible="visible"
    title="投诉风险识别记录"
    :footer="null"
    :maskClosable="true"
    :destroyOnClose="true"
    width="800px"
    centered
    wrapClassName="complaint-risk-modal"
    @cancel="handleClose"
  >
    <!-- 提示条：风险等级未知时隐藏 -->
    <div
      v-if="tipConfig"
      class="risk-tip"
      :style="{ color: tipConfig.color, background: tipConfig.bg }"
    >
      {{ tipConfig.text }}
    </div>
    <div class="record-list">
      <div v-for="(record, index) in records" :key="record.recordId || index" class="record-card">
        <div class="record-rows">
          <div v-for="row in getRecordRows(record)" :key="row.label" class="record-row">
            <span class="record-row__label">{{ row.label }}：</span>
            <span class="record-row__value">{{ row.value }}</span>
          </div>
        </div>
        <template v-if="shouldShowDetailButton(record)">
          <div class="record-divider"></div>
          <div class="record-actions">
            <a-button class="detail-btn" @click="handleViewDetail(record)">查看详情</a-button>
          </div>
        </template>
      </div>
      <a-empty v-if="!records.length" description="暂无数据" />
    </div>
  </a-modal>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'
import {
  CALL_SOURCE_LABEL,
  DETAIL_SOURCE_LABEL,
  formatCallDuration,
  getSourceLabelText
} from '../model/complaintRisk'
import { toDisplayValue } from '../model/portraitView'

defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  // 顶部提示条配置（文案/文字色/背景色），为 null 时隐藏
  tipConfig: {
    type: Object,
    default: null
  },
  records: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:visible'])

// 卡片行数据：电话来源展示通话信息并隐藏投诉内容
const getRecordRows = record => {
  const rows = [
    { label: '投诉场景', value: toDisplayValue(getSourceLabelText(record.sourceLabel)) },
    { label: '投诉时间', value: toDisplayValue(record.occurredAt) }
  ]
  if (record.sourceLabel === CALL_SOURCE_LABEL) {
    rows.push(
      { label: '通话时长', value: formatCallDuration(record.callDuration) },
      { label: '呼出人', value: toDisplayValue(record.caller) },
      { label: '接听人', value: toDisplayValue(record.callee) }
    )
  } else {
    rows.push({ label: '投诉内容', value: toDisplayValue(record.content) })
  }
  return rows
}

// 仅投诉与建议来源展示「查看详情」
const shouldShowDetailButton = record => record.sourceLabel === DETAIL_SOURCE_LABEL

const handleClose = () => {
  emit('update:visible', false)
}

const handleViewDetail = record => {
  window.open(
    process.env.VUE_APP_CMS_URL + `/operationsManage/feedback/detail/${record.sourceBusinessId}`
  )
}
</script>

<style lang="scss" scoped>
.risk-tip {
  flex-shrink: 0;
  padding: 12px 24px;
  font-size: 16px;
  line-height: 22px;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px 24px;
  // 列表区占满剩余空间并独立滚动，提示条固定在顶部
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.record-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 14px;

  .record-rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .record-row {
    display: flex;
    font-size: 16px;
    line-height: 22px;
    font-weight: 400;

    &__label {
      flex-shrink: 0;
      color: #999999;
    }

    &__value {
      min-width: 0;
      color: #333333;
      word-break: break-all;
    }
  }

  .record-divider {
    height: 1px;
    background: #efefef;
    margin: 10px 0;
  }

  .record-actions {
    display: flex;
    justify-content: flex-end;

    .detail-btn {
      padding: 6px 12px;
      border: 1px solid #dbdbdb;
      border-radius: 8px;
      color: #1a1a1a;
      font-size: 14px;
      line-height: 20px;
    }
  }
}
</style>

<style lang="scss">
.complaint-risk-modal {
  .ant-modal-body {
    background: #f5f5f5;
    padding: 0;
    // 限高后提示条固定顶部，滚动交给内部列表区
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}
</style>
